/**
 * DiagnosticsDrawer: section 5 of the Screener page -- ONE drawer for every audit / diagnostic block. It opens itself on
 * any non-pass state (ScreenView owns diagOpen). Moved verbatim out of src/pages/ScreenView.tsx (Screener Phase 3,
 * 2026-10-28); its eight blocks live in ./diagnostics. Display only.
 */
import React from "react";
import { cardStyle, mutedText, secondaryText, ui } from "@components/screener/screenerUi";
import { Chip } from "@components/cockpit";
import DiagRunStatus from "@components/screener/diagnostics/DiagRunStatus";
import DiagContractQuality from "@components/screener/diagnostics/DiagContractQuality";
import DiagRankIntegrity from "@components/screener/diagnostics/DiagRankIntegrity";
import DiagQuoteHydration from "@components/screener/diagnostics/DiagQuoteHydration";
import DiagAuditTrail from "@components/screener/diagnostics/DiagAuditTrail";
import DiagPostOiLedger from "@components/screener/diagnostics/DiagPostOiLedger";
import DiagSurface from "@components/screener/diagnostics/DiagSurface";
import DiagDistributions from "@components/screener/diagnostics/DiagDistributions";
import { type AllocationValidation, type AllocationValidationViolation, type ChainAudit, type DataConfidence, type DecisionContext, type FilterAudit, type FilterAuditExpiryCoverageRow, type FilterAuditWaterfallRow, type Purchase, type ScreenerResponse, type SurfaceSummary, type Tone, type UniverseGrade } from "@lib/screenerTypes";

export type DiagnosticsDrawerProps = {
  allCandidateRows: Purchase[];
  allocationValidation: AllocationValidation | null;
  allocationValidationViolations: AllocationValidationViolation[];
  auditWaterfall: FilterAuditWaterfallRow[];
  candidateUniverseSummary: { total: number; selected: number; ranked: number; skipped: number; };
  chainAudit: ChainAudit | null;
  chainExpiryPlan: Record<string, any>[];
  contractQualityGrade: UniverseGrade;
  data: ScreenerResponse;
  dataConfidence: DataConfidence | null;
  decisionContext: DecisionContext | null;
  diagHeadline: { label: string; detail: string; tone: "pass" | "warn" | "fail"; };
  diagOpen: boolean;
  farthestExpiryCoverage: FilterAuditExpiryCoverageRow[];
  filterAudit: FilterAudit | null;
  guardrailRows: { label: string; tone: Tone; status: string; detail: string; }[];
  marketRankOnly: boolean;
  maxTenorPct: number;
  mlWeight: number | null;
  moneynessBehavior: { baseBand: number; bands: { label: string; upper: number | null; count: number; pct: number; }[]; expansionActive: boolean; widestUsed: number; };
  postOiUniverseRows: Purchase[];
  postOiUniverseSummary: { total: number; quoteOk: number; needsQuote: number; farthest: string; };
  quoteHydration: any;
  quoteHydrationSummary: { resolved: number; rowsReturned: number; quoteComplete: number; greekComplete: number; fullComplete: number; incomplete: number; pct: number | null; mode: string; elapsedMs: number; singletonAttempted: number; statusCounts: any; };
  quoteHydrationTone: "neutral" | "success" | "warning" | "danger";
  setDiagUserOpen: React.Dispatch<React.SetStateAction<boolean | null>>;
  surfaceSummary: SurfaceSummary | undefined;
  tenorMixRows: { bucket: string; label: string; count: number; pct: number; tone: Tone; }[];
};

export default function DiagnosticsDrawer({
  allCandidateRows,
  allocationValidation,
  allocationValidationViolations,
  auditWaterfall,
  candidateUniverseSummary,
  chainAudit,
  chainExpiryPlan,
  contractQualityGrade,
  data,
  dataConfidence,
  decisionContext,
  diagHeadline,
  diagOpen,
  farthestExpiryCoverage,
  filterAudit,
  guardrailRows,
  marketRankOnly,
  maxTenorPct,
  mlWeight,
  moneynessBehavior,
  postOiUniverseRows,
  postOiUniverseSummary,
  quoteHydration,
  quoteHydrationSummary,
  quoteHydrationTone,
  setDiagUserOpen,
  surfaceSummary,
  tenorMixRows,
}: DiagnosticsDrawerProps) {
  return (
    <section
      data-screener-section="diagnostics"
      data-testid="screener-diagnostics"
      data-open={diagOpen ? "true" : "false"}
      className="mb-4 rounded-xl"
      style={cardStyle}
    >
      <button
        type="button"
        className="flex w-full flex-wrap items-center gap-2 px-3.5 py-3 text-left"
        aria-expanded={diagOpen}
        data-testid="screener-diagnostics-toggle"
        onClick={() => setDiagUserOpen(!diagOpen)}
      >
        <span aria-hidden style={mutedText}>{diagOpen ? "▾" : "▸"}</span>
        <span className="font-semibold">Diagnostics:</span>
        <span data-testid="screener-diagnostics-status" data-tone={diagHeadline.tone}>
          <Chip tone={diagHeadline.tone === "pass" ? "positive" : diagHeadline.tone === "fail" ? "danger" : "warning"}>
            {diagHeadline.label}
          </Chip>
        </span>
        {diagHeadline.detail ? (
          <span className="text-xs" style={secondaryText}>
            {diagHeadline.detail}
          </span>
        ) : (
          <span className="text-xs" style={mutedText}>
            data confidence, quotes, integrity, audit trail, universe ledger, surface, contract quality
          </span>
        )}
      </button>
      <div hidden={!diagOpen} data-testid="screener-diagnostics-body" className="space-y-4 border-t px-3.5 pb-4 pt-3" style={{ borderColor: ui.cardBorder }}>
        {/* -- Run status -- */}
        <DiagRunStatus
          allocationValidationViolations={allocationValidationViolations}
          dataConfidence={dataConfidence}
          decisionContext={decisionContext}
          mlWeight={mlWeight}
        />

        {/* -- Universe contract quality (CIO decision 4: moved to diagnostics, labelled tenor-driven) -- */}
        <DiagContractQuality
          candidateUniverseSummary={candidateUniverseSummary}
          contractQualityGrade={contractQualityGrade}
        />

        {/* -- Market-Rank Integrity (the allocator guardrail rows are cut; this is the rank-mode set) -- */}
        {marketRankOnly ? (
          <DiagRankIntegrity
            guardrailRows={guardrailRows}
          />
        ) : null}

        {/* -- Quote hydration: shown ONCE (it used to appear six times). -- */}
        <DiagQuoteHydration
          chainAudit={chainAudit}
          quoteHydration={quoteHydration}
          quoteHydrationSummary={quoteHydrationSummary}
          quoteHydrationTone={quoteHydrationTone}
        />

        {/* -- Audit trail: chain discovery, filter waterfall, chain resolver -- */}
        {(filterAudit || chainAudit) && (
          <DiagAuditTrail
            auditWaterfall={auditWaterfall}
            chainAudit={chainAudit}
            chainExpiryPlan={chainExpiryPlan}
            data={data}
            farthestExpiryCoverage={farthestExpiryCoverage}
            filterAudit={filterAudit}
          />
        )}

        {/* -- Full universe after moneyness + OI (the post-OI ledger) -- */}
        {postOiUniverseRows.length > 0 && (
          <DiagPostOiLedger
            candidateUniverseSummary={candidateUniverseSummary}
            postOiUniverseRows={postOiUniverseRows}
            postOiUniverseSummary={postOiUniverseSummary}
          />
        )}

        {/* -- Surface diagnostics -- */}
        <DiagSurface
          allocationValidation={allocationValidation}
          allocationValidationViolations={allocationValidationViolations}
          candidateUniverseSummary={candidateUniverseSummary}
          data={data}
          marketRankOnly={marketRankOnly}
          surfaceSummary={surfaceSummary}
        />

        {/* -- Tenor & moneyness counts across the ranked universe (one copy; the surface badge copy is cut) -- */}
      <DiagDistributions
        allCandidateRows={allCandidateRows}
        maxTenorPct={maxTenorPct}
        moneynessBehavior={moneynessBehavior}
        tenorMixRows={tenorMixRows}
      />
      </div>
    </section>
  );
}
